/**
 * `mouseApi`: the RTK Query slice for the Mouse ordering service.
 *
 * Its own slice rather than more endpoints on `zion`: Connect's shared
 * `axiosBaseQuery` concatenates one base URL per slice, and the ordering
 * endpoints already form a self-contained block with their own tags (see
 * `mouse/docs/preorder-map/connect-and-stripe.md` A0). The staff bearer is the
 * same Morpheus token `customAxios` already attaches for Zion.
 *
 * Errors come back as `{ status, data: { code, error } }`; pages read them with
 * `mouseErrorMessage` / `isSquareOwnedError` from `mouse.errors.ts`.
 */
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { createApi, retry } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import config from '@/config'
import { customAxios } from '@/utils/axiosHelper'
import { shouldBailout } from '../helpers'
import { resolveMouseBaseUrl } from './mouse.base-url'
import { mouseUrls } from './mouse.urls'
import { toCursorPage, toList } from './mouse.transforms'
import { applyMoves, isMovesOnly } from './venue-moves'
import type {
  BulkPointsBody,
  BulkPointsResult,
  CataloguePiece,
  CrossSellInput,
  CrossSellsResult,
  MenuReplay,
  MenuReplayQuery,
  MenuReport,
  MenuReportQuery,
  VenueCommandBody,
  VenueDispatchResult,
  VenueSpaceDoc,
  MenuVenueZonesResult,
  VenueZones,
  Catalogue,
  CreateMenuBody,
  AppliedMenu,
  GeneratedMenuPreview,
  CreateModifierBody,
  CreateModifierGroupBody,
  CreateProductBody,
  CreatePointBody,
  CreateSectionBody,
  CreateTagBody,
  CursorPage,
  HoursResult,
  HoursWindow,
  Media,
  Menu,
  MenuPolicy,
  MenuSection,
  MenuImportCommitBody,
  MenuImportCommitResult,
  PaymentAccounts,
  StripePaymentAccount,
  AddStripeAccountBody,
  MenuImportJob,
  MenuSummary,
  StartMenuImportBody,
  Modifier,
  ModifierGroup,
  ModifierGroupLinkBody,
  ModifierHideResult,
  MouseQueryError,
  Order,
  OrderSummary,
  OrdersSummary,
  OrdersSummaryQuery,
  SmsLog,
  SmsLogQuery,
  AppFeedback,
  AppFeedbackQuery,
  OrderingCapabilities,
  OrderingPoint,
  OrderingVenueSettings,
  OrdersQuery,
  PatchMenuBody,
  PatchMenuPolicyBody,
  PatchModifierBody,
  PatchModifierGroupBody,
  PatchPointBody,
  PatchProductBody,
  PatchSectionBody,
  PatchSectionProductBody,
  PatchVariantBody,
  PointQr,
  Product,
  ProductDetail,
  ProductOverrideBody,
  ProductsQuery,
  ProductVariant,
  RefundBody,
  RefundResult,
  ReplaceResult,
  SquareCatalogue,
  StampedeCatalogue,
  SyncJob,
  Tag,
  VariantBody,
} from './mouse.types'

export const MOUSE_BASE_URL = resolveMouseBaseUrl(config.url.mouse)

interface MouseRequest {
  url: string
  method: AxiosRequestConfig['method']
  data?: AxiosRequestConfig['data']
}

const mouseBaseQuery =
  (baseUrl: string): BaseQueryFn<MouseRequest, unknown, MouseQueryError> =>
  async ({ url, method, data }) => {
    try {
      const result = await customAxios({
        url: baseUrl + url,
        method,
        data,
      })
      return { data: result.data }
    } catch (caught) {
      const err = caught as AxiosError<MouseQueryError['data']>
      const status = err.response?.status
      const body = err.response?.data
      // A 4xx is the caller's problem and never worth a retry; `retry` reads
      // this to stop early.
      if (shouldBailout(err)) {
        return retry.fail({ status, data: body ?? err.message })
      }
      return { error: { status, data: body ?? err.message } }
    }
  }

const mouseApi = createApi({
  reducerPath: 'mouse',
  baseQuery: retry(mouseBaseQuery(MOUSE_BASE_URL), { maxRetries: 2 }),
  tagTypes: [
    'Capabilities',
    'Menus',
    'Menu',
    'Catalogue',
    'Products',
    'Product',
    'Modifiers',
    'Points',
    'VenueSettings',
    'Orders',
    'Order',
    'MenuImports',
    'MenuImport',
    'PaymentAccounts',
    'Venue',
    'Messages',
    'AppFeedback',
  ],
  endpoints: (builder) => ({
    // ── Capabilities ────────────────────────────────────────────────────────
    getOrderingCapabilities: builder.query<OrderingCapabilities, string>({
      query: (orgId) => ({ url: mouseUrls.capabilities(orgId), method: 'GET' }),
      providesTags: ['Capabilities'],
    }),

    // ── Menus ───────────────────────────────────────────────────────────────
    getMenus: builder.query<MenuSummary[], { orgId: string; venue?: string }>({
      query: ({ orgId, venue }) => ({
        url: mouseUrls.menus(orgId, venue),
        method: 'GET',
      }),
      transformResponse: (raw: MenuSummary[] | { data: MenuSummary[] }) =>
        toList(raw),
      providesTags: ['Menus'],
    }),
    getMenu: builder.query<
      Menu,
      { orgId: string; menuId: string; venue?: string }
    >({
      query: ({ orgId, menuId, venue }) => ({
        url: mouseUrls.menu(orgId, menuId, venue),
        method: 'GET',
      }),
      providesTags: (_r, _e, { menuId }) => [{ type: 'Menu', id: menuId }],
    }),
    /**
     * A menu's report. Read-only and never cached against the menu's own tag:
     * editing a menu does not change what it sold yesterday, and invalidating
     * the report on every menu edit would refetch a whole day's figures for
     * nothing.
     */
    getMenuReport: builder.query<
      MenuReport,
      { orgId: string; menuId: string } & MenuReportQuery
    >({
      query: ({ orgId, menuId, date, from, to }) => ({
        url: mouseUrls.menuReport(orgId, menuId, { date, from, to }),
        method: 'GET',
      }),
      /**
       * NEVER RETRIED, unlike every other call on this slice.
       *
       * The report is the heaviest read in the product: it fans out to
       * twenty-two queries against a pool of ten. When it fails it is
       * because that pool was exhausted, and a 500 is not a 4xx so the
       * usual bail-out does not catch it. Retrying twice turns one page
       * load into sixty-six queries and three rounds of holding every
       * connection in the process, while guests wait behind it to pay.
       *
       * The failure is not transient and the retry is what makes it an
       * outage rather than an error message (2026-10-03).
       */
      extraOptions: { maxRetries: 0 },
    }),
    /**
     * A menu's day as a replay. Read-only, and untagged for the same reason
     * the report is: editing a menu does not change what it sold yesterday.
     */
    getMenuReplay: builder.query<
      MenuReplay,
      { orgId: string; menuId: string } & MenuReplayQuery
    >({
      query: ({ orgId, menuId, serial, date, from, to }) => ({
        url: mouseUrls.menuReplay(orgId, menuId, { serial, date, from, to }),
        method: 'GET',
      }),
    }),
    getVenueCatalogue: builder.query<
      { items: CataloguePiece[] },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: mouseUrls.venueSpaceCatalogue(orgId),
        method: 'GET',
      }),
    }),
    getVenueSpace: builder.query<
      VenueSpaceDoc,
      { orgId: string; serial: string }
    >({
      query: ({ orgId, serial }) => ({
        url: mouseUrls.venueSpace(orgId, serial),
        method: 'GET',
      }),
      providesTags: (_r, _e, { serial }) => [{ type: 'Venue', id: serial }],
    }),
    createVenueSpace: builder.mutation<
      VenueSpaceDoc,
      { orgId: string; serial: string; name: string }
    >({
      query: ({ orgId, serial, name }) => ({
        url: mouseUrls.venueSpace(orgId, serial),
        method: 'POST',
        data: { name },
      }),
      invalidatesTags: (_r, _e, { serial }) => [{ type: 'Venue', id: serial }],
    }),
    /**
     * One gesture, one batch, one undo step. Undo is a batch like any other
     * carrying the inverse commands, which is why there is no undo endpoint.
     *
     * NOT tag-invalidating: the page holds the document locally and applies
     * optimistically, so a refetch here would overwrite a drag in progress
     * with a slower round trip.
     */
    dispatchVenueCommands: builder.mutation<
      VenueDispatchResult,
      { orgId: string; serial: string } & VenueCommandBody
    >({
      query: ({ orgId, serial, ...body }) => ({
        url: mouseUrls.venueSpaceCommands(orgId, serial),
        method: 'POST',
        data: body,
      }),
      /**
       * Keep the cache in step, and for a MOVE do it before the request.
       *
       * This is not an optimisation, it is the feature working at all: with
       * neither this nor `invalidatesTags` a move saved correctly and the page
       * never learned, so the preview cleared on release and the piece snapped
       * back to where it started.
       *
       * A MOVE IS APPLIED OPTIMISTICALLY. The client already knows exactly
       * where the piece went, and a move is a total assignment of three
       * numbers, so applying it cannot diverge from what the server does with
       * the same command. The piece stays where it was dropped, the request
       * confirms it, and a refusal undoes the patch. Only `version` and
       * `issues` are taken from the response, so the pieces keep their object
       * identities and the renderer skips the ones that did not move. Writing
       * the whole document back reconciled the entire scene, chairs and all,
       * every time somebody let go of a table.
       *
       * EVERYTHING ELSE TAKES THE SERVER'S DOCUMENT. An add, a remove, a
       * floor change, a re-spec: those touch the unplaced tray and fields the
       * client would have to guess at, and they are not the hot path.
       */
      async onQueryStarted(
        { orgId, serial, commands },
        { dispatch, queryFulfilled }
      ) {
        const optimistic = isMovesOnly(commands)
          ? dispatch(
              mouseApi.util.updateQueryData(
                'getVenueSpace',
                { orgId, serial },
                (draft) => {
                  applyMoves(draft.pieces, commands)
                }
              )
            )
          : null
        try {
          const { data } = await queryFulfilled
          dispatch(
            mouseApi.util.updateQueryData(
              'getVenueSpace',
              { orgId, serial },
              (draft) => {
                if (optimistic) {
                  // The move is already in. Take only what the server alone
                  // can know: the new version, and what it thinks is wrong.
                  draft.version = data.doc.version
                  draft.issues = data.doc.issues
                  return undefined
                }
                return data.doc
              }
            )
          )
        } catch {
          // A refused version is the one case where the page cannot
          // reconcile, so the patch comes out and the caller re-fetches.
          optimistic?.undo()
        }
      },
    }),
    publishVenueSpace: builder.mutation<
      VenueSpaceDoc,
      { orgId: string; serial: string }
    >({
      query: ({ orgId, serial }) => ({
        url: mouseUrls.venueSpacePublish(orgId, serial),
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { serial }) => [{ type: 'Venue', id: serial }],
    }),
    getMenuCrossSells: builder.query<
      CrossSellsResult,
      { orgId: string; menuId: string }
    >({
      query: ({ orgId, menuId }) => ({
        url: mouseUrls.menuCrossSells(orgId, menuId),
        method: 'GET',
      }),
      providesTags: (_r, _e, { menuId }) => [
        { type: 'Menu', id: `xsell-${menuId}` },
      ],
    }),
    /**
     * Replace the whole list. Position comes from the ARRAY ORDER, so the
     * editor never has to track row ids or renumber anything.
     */
    setMenuCrossSells: builder.mutation<
      CrossSellsResult,
      { orgId: string; menuId: string; cross_sells: CrossSellInput[] }
    >({
      query: ({ orgId, menuId, cross_sells }) => ({
        url: mouseUrls.menuCrossSells(orgId, menuId),
        method: 'PUT',
        data: { cross_sells },
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        { type: 'Menu', id: `xsell-${menuId}` },
      ],
    }),
    createMenu: builder.mutation<
      MenuSummary,
      { orgId: string; body: CreateMenuBody }
    >({
      query: ({ orgId, body }) => ({
        url: mouseUrls.menus(orgId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Menus'],
    }),
    // Preview only: builds a draft from the venue's live Square catalogue and
    // returns it. It writes nothing, so it invalidates no cache.
    generateMenu: builder.mutation<
      GeneratedMenuPreview,
      { orgId: string; serial: string }
    >({
      query: ({ orgId, serial }) => ({
        url: mouseUrls.generateMenu(orgId),
        method: 'POST',
        data: { serial },
      }),
    }),
    // Persists a generated draft as a real (inactive) menu.
    applyGeneratedMenu: builder.mutation<
      AppliedMenu,
      { orgId: string; serial: string; name?: string }
    >({
      query: ({ orgId, serial, name }) => ({
        url: mouseUrls.generateMenuApply(orgId),
        method: 'POST',
        data: name ? { serial, name } : { serial },
      }),
      invalidatesTags: ['Menus'],
    }),
    // ── Menu import ─────────────────────────────────────────────────────────
    /**
     * Starts an import. Answers 202 with the job, whose extraction is already
     * running: reading twenty pages takes minutes, so the dialog polls
     * `getMenuImport` rather than holding a request open.
     */
    startMenuImport: builder.mutation<
      MenuImportJob,
      { orgId: string; body: StartMenuImportBody }
    >({
      query: ({ orgId, body }) => ({
        url: mouseUrls.menuImports(orgId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['MenuImports'],
    }),
    getMenuImports: builder.query<
      MenuImportJob[],
      { orgId: string; venue: string; limit?: number }
    >({
      query: ({ orgId, venue, limit }) => ({
        url: mouseUrls.menuImports(orgId, venue, limit),
        method: 'GET',
      }),
      providesTags: ['MenuImports'],
    }),
    getMenuImport: builder.query<
      MenuImportJob,
      { orgId: string; importId: string }
    >({
      query: ({ orgId, importId }) => ({
        url: mouseUrls.menuImport(orgId, importId),
        method: 'GET',
      }),
      providesTags: (_r, _e, { importId }) => [
        { type: 'MenuImport', id: importId },
      ],
    }),
    /** Writes the menu. Invalidates `Menus`, because one now exists. */
    commitMenuImport: builder.mutation<
      MenuImportCommitResult,
      { orgId: string; importId: string; body: MenuImportCommitBody }
    >({
      query: ({ orgId, importId, body }) => ({
        url: mouseUrls.menuImportCommit(orgId, importId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_r, _e, { importId }) => [
        'Menus',
        'MenuImports',
        { type: 'MenuImport', id: importId },
      ],
    }),
    patchMenu: builder.mutation<
      MenuSummary,
      { orgId: string; menuId: string; body: PatchMenuBody }
    >({
      query: ({ orgId, menuId, body }) => ({
        url: mouseUrls.menu(orgId, menuId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),
    putMenuVenues: builder.mutation<
      { menu_id: string; venues: string[] },
      { orgId: string; menuId: string; venues: string[] }
    >({
      query: ({ orgId, menuId, venues }) => ({
        url: mouseUrls.menuVenues(orgId, menuId),
        method: 'PUT',
        data: { venues },
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),
    /**
     * The zones a menu is live in AT ONE VENUE. An empty list clears the
     * restriction (every zone there, unzoned tables included).
     */
    putMenuVenueZones: builder.mutation<
      MenuVenueZonesResult,
      { orgId: string; menuId: string; serial: string; zones: string[] }
    >({
      query: ({ orgId, menuId, serial, zones }) => ({
        url: mouseUrls.menuVenueZones(orgId, menuId, serial),
        method: 'PUT',
        data: { zones },
      }),
      invalidatesTags: (_r, _e, { menuId }) => [{ type: 'Menu', id: menuId }],
    }),
    /** Whole-row replace: the policy form sends every field. */
    putMenuPolicy: builder.mutation<
      MenuPolicy,
      { orgId: string; menuId: string; policy: MenuPolicy }
    >({
      query: ({ orgId, menuId, policy }) => ({
        url: mouseUrls.menuPolicy(orgId, menuId),
        method: 'PUT',
        data: policy,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),
    /** Partial: a list-page toggle changes one key and leaves the rest alone. */
    patchMenuPolicy: builder.mutation<
      MenuPolicy,
      { orgId: string; menuId: string; body: PatchMenuPolicyBody }
    >({
      query: ({ orgId, menuId, body }) => ({
        url: mouseUrls.menuPolicy(orgId, menuId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),
    duplicateMenu: builder.mutation<
      MenuSummary,
      { orgId: string; menuId: string }
    >({
      query: ({ orgId, menuId }) => ({
        url: mouseUrls.menuDuplicate(orgId, menuId),
        method: 'POST',
      }),
      invalidatesTags: ['Menus'],
    }),
    deleteMenu: builder.mutation<unknown, { orgId: string; menuId: string }>({
      query: ({ orgId, menuId }) => ({
        url: mouseUrls.menu(orgId, menuId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Menus'],
    }),

    /** Replaces the menu's header, gallery and creation images. Mouse takes the list as the body itself. */
    putMenuMedia: builder.mutation<
      { menu_id: string; media: Media[] },
      { orgId: string; menuId: string; media: Media[] }
    >({
      query: ({ orgId, menuId, media }) => ({
        url: mouseUrls.menuMedia(orgId, menuId),
        method: 'PUT',
        data: media,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),

    /**
     * Replaces one section's images. The header is the picture on the
     * section's card when the menu browses as cards, which is how a food hall
     * shows its vendors.
     */
    putSectionMedia: builder.mutation<
      { menu_id: string; section_id: string; media: Media[] },
      { orgId: string; menuId: string; sectionId: string; media: Media[] }
    >({
      query: ({ orgId, menuId, sectionId, media }) => ({
        url: mouseUrls.sectionMedia(orgId, menuId, sectionId),
        method: 'PUT',
        data: media,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),

    // Hours: each PUT replaces the target's whole week (empty = always available).
    putMenuHours: builder.mutation<
      HoursResult,
      { orgId: string; menuId: string; hours: HoursWindow[] }
    >({
      query: ({ orgId, menuId, hours }) => ({
        url: mouseUrls.menuHours(orgId, menuId),
        method: 'PUT',
        data: { hours },
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),
    putSectionHours: builder.mutation<
      HoursResult,
      { orgId: string; menuId: string; sectionId: string; hours: HoursWindow[] }
    >({
      query: ({ orgId, menuId, sectionId, hours }) => ({
        url: mouseUrls.sectionHours(orgId, menuId, sectionId),
        method: 'PUT',
        data: { hours },
      }),
      invalidatesTags: (_r, _e, { menuId }) => [{ type: 'Menu', id: menuId }],
    }),
    putProductHours: builder.mutation<
      HoursResult,
      { orgId: string; productId: string; hours: HoursWindow[] }
    >({
      query: ({ orgId, productId, hours }) => ({
        url: mouseUrls.productHours(orgId, productId),
        method: 'PUT',
        data: { hours },
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        'Menu',
        { type: 'Product', id: productId },
      ],
    }),

    // Sections
    createSection: builder.mutation<
      MenuSection,
      { orgId: string; menuId: string; body: CreateSectionBody }
    >({
      query: ({ orgId, menuId, body }) => ({
        url: mouseUrls.sections(orgId, menuId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),
    patchSection: builder.mutation<
      MenuSection,
      {
        orgId: string
        menuId: string
        sectionId: string
        body: PatchSectionBody
      }
    >({
      query: ({ orgId, menuId, sectionId, body }) => ({
        url: mouseUrls.section(orgId, menuId, sectionId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [{ type: 'Menu', id: menuId }],
    }),
    deleteSection: builder.mutation<
      unknown,
      { orgId: string; menuId: string; sectionId: string }
    >({
      query: ({ orgId, menuId, sectionId }) => ({
        url: mouseUrls.section(orgId, menuId, sectionId),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { menuId }) => [
        'Menus',
        { type: 'Menu', id: menuId },
      ],
    }),
    orderSections: builder.mutation<
      unknown,
      { orgId: string; menuId: string; sectionIds: string[] }
    >({
      query: ({ orgId, menuId, sectionIds }) => ({
        url: mouseUrls.sectionsOrder(orgId, menuId),
        method: 'PUT',
        data: { section_ids: sectionIds },
      }),
      invalidatesTags: (_r, _e, { menuId }) => [{ type: 'Menu', id: menuId }],
    }),
    /**
     * Replaces a section's product list. Optimistic: the cached menu detail is
     * rewritten at once so a removed row disappears on click and a re-order
     * settles where it was dropped; a failure puts the previous list back and
     * the tag invalidation then refetches the truth either way. `venue` is not
     * sent; it is the menu detail's cache key on the venue-scoped mount.
     */
    putSectionProducts: builder.mutation<
      {
        section_id: string
        products: { product_id: string; position: number }[]
      },
      {
        orgId: string
        menuId: string
        sectionId: string
        productIds: string[]
        venue?: string
      }
    >({
      query: ({ orgId, menuId, sectionId, productIds }) => ({
        url: mouseUrls.sectionProducts(orgId, menuId, sectionId),
        method: 'PUT',
        data: { product_ids: productIds },
      }),
      async onQueryStarted(
        { orgId, menuId, sectionId, productIds, venue },
        { dispatch, queryFulfilled }
      ) {
        const patch = dispatch(
          mouseApi.util.updateQueryData(
            'getMenu',
            { orgId, menuId, venue },
            (draft) => {
              const section = draft.sections.find((s) => s.id === sectionId)
              if (!section?.products) return
              const byId = new Map(
                section.products.map((p) => [p.product_id, p])
              )
              section.products = productIds
                .map((id, position) => {
                  const row = byId.get(id)
                  return row ? { ...row, position } : null
                })
                .filter((row): row is NonNullable<typeof row> => row !== null)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_r, _e, { menuId }) => [{ type: 'Menu', id: menuId }],
    }),
    patchSectionProduct: builder.mutation<
      unknown,
      {
        orgId: string
        menuId: string
        sectionId: string
        productId: string
        body: PatchSectionProductBody
      }
    >({
      query: ({ orgId, menuId, sectionId, productId, body }) => ({
        url: mouseUrls.sectionProduct(orgId, menuId, sectionId, productId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_r, _e, { menuId }) => [{ type: 'Menu', id: menuId }],
    }),

    // ── Money ───────────────────────────────────────────────────────────────
    getPaymentAccounts: builder.query<PaymentAccounts, string>({
      query: (orgId) => ({
        url: mouseUrls.paymentAccounts(orgId),
        method: 'GET',
      }),
      providesTags: ['PaymentAccounts'],
    }),
    addStripePaymentAccount: builder.mutation<
      StripePaymentAccount,
      { orgId: string; body: AddStripeAccountBody }
    >({
      query: ({ orgId, body }) => ({
        url: mouseUrls.stripePaymentAccount(orgId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['PaymentAccounts'],
    }),
    deactivateStripePaymentAccount: builder.mutation<
      { id: string; active: false; menus: number },
      { orgId: string; integrationId: string }
    >({
      query: ({ orgId, integrationId }) => ({
        url: mouseUrls.stripePaymentAccount(orgId, integrationId),
        method: 'DELETE',
      }),
      // The menus keep pointing at it on purpose (Mouse does not move them),
      // so their own reads have to come back for the new state.
      invalidatesTags: ['PaymentAccounts', 'Menus', 'Menu'],
    }),

    // ── Catalogue ───────────────────────────────────────────────────────────
    getCatalogues: builder.query<Catalogue[], string>({
      query: (orgId) => ({ url: mouseUrls.catalogues(orgId), method: 'GET' }),
      transformResponse: (raw: Catalogue[] | { data: Catalogue[] }) =>
        toList(raw),
      providesTags: ['Catalogue'],
    }),
    /**
     * Connects a venue's Square catalogue so it can be synced.
     *
     * Until this existed the only thing that ever created a Square catalogue
     * was the Zion migration script, so a venue that was never migrated had
     * no catalogue, no Sync from Square button, and no way in from the
     * product. Mouse asks Neo which merchant the venue belongs to.
     */
    connectSquareCatalogue: builder.mutation<
      SquareCatalogue,
      { orgId: string; serial: string }
    >({
      query: ({ orgId, serial }) => ({
        url: mouseUrls.squareCatalogue(orgId),
        method: 'POST',
        data: { serial },
      }),
      invalidatesTags: ['Capabilities', 'Catalogue'],
    }),
    // Authoring: the org's own products, groups and modifiers (Mouse-authority catalogues only).
    ensureStampedeCatalogue: builder.mutation<
      StampedeCatalogue,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: mouseUrls.stampedeCatalogue(orgId),
        method: 'POST',
      }),
      invalidatesTags: ['Capabilities', 'Catalogue'],
    }),
    /** `catalogueId` null means the Stampede catalogue, created if the org has none. */
    createProduct: builder.mutation<
      Product,
      { orgId: string; catalogueId: string | null; body: CreateProductBody }
    >({
      query: ({ orgId, catalogueId, body }) => ({
        url: catalogueId
          ? mouseUrls.catalogueProducts(orgId, catalogueId, {})
          : mouseUrls.products(orgId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Capabilities', 'Catalogue', 'Products'],
    }),
    deleteProduct: builder.mutation<
      unknown,
      { orgId: string; productId: string }
    >({
      query: ({ orgId, productId }) => ({
        url: mouseUrls.product(orgId, productId),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Catalogue',
        'Products',
        'Menus',
        { type: 'Product', id: productId },
      ],
    }),
    putProductModifierGroups: builder.mutation<
      { product_id: string; groups: ModifierGroupLinkBody[] },
      { orgId: string; productId: string; groups: ModifierGroupLinkBody[] }
    >({
      query: ({ orgId, productId, groups }) => ({
        url: mouseUrls.productModifierGroups(orgId, productId),
        method: 'PUT',
        data: { groups },
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    replaceProduct: builder.mutation<
      ReplaceResult,
      { orgId: string; productId: string; withProductId: string }
    >({
      query: ({ orgId, productId, withProductId }) => ({
        url: mouseUrls.productReplaceWith(orgId, productId),
        method: 'POST',
        data: { product_id: withProductId },
      }),
      invalidatesTags: ['Catalogue', 'Products', 'Product', 'Menus', 'Menu'],
    }),
    createVariant: builder.mutation<
      ProductVariant,
      { orgId: string; productId: string; body: VariantBody }
    >({
      query: ({ orgId, productId, body }) => ({
        url: mouseUrls.productVariants(orgId, productId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    patchVariant: builder.mutation<
      ProductVariant,
      {
        orgId: string
        productId: string
        variantId: string
        body: PatchVariantBody
      }
    >({
      query: ({ orgId, variantId, body }) => ({
        url: mouseUrls.variant(orgId, variantId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    deleteVariant: builder.mutation<
      unknown,
      { orgId: string; productId: string; variantId: string }
    >({
      query: ({ orgId, variantId }) => ({
        url: mouseUrls.variant(orgId, variantId),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    createModifierGroup: builder.mutation<
      ModifierGroup,
      { orgId: string; catalogueId: string; body: CreateModifierGroupBody }
    >({
      query: ({ orgId, catalogueId, body }) => ({
        url: mouseUrls.catalogueModifierGroups(orgId, catalogueId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Catalogue', 'Modifiers'],
    }),
    createModifier: builder.mutation<
      Modifier,
      { orgId: string; groupId: string; body: CreateModifierBody }
    >({
      query: ({ orgId, groupId, body }) => ({
        url: mouseUrls.groupModifiers(orgId, groupId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Modifiers'],
    }),
    deleteModifierGroup: builder.mutation<
      unknown,
      { orgId: string; groupId: string }
    >({
      query: ({ orgId, groupId }) => ({
        url: mouseUrls.modifierGroup(orgId, groupId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Catalogue', 'Modifiers', 'Products', 'Product'],
    }),
    setModifierGroupHidden: builder.mutation<
      ModifierHideResult,
      { orgId: string; groupId: string; hidden: boolean }
    >({
      query: ({ orgId, groupId, hidden }) => ({
        url: mouseUrls.modifierGroupHidden(orgId, groupId),
        method: 'PUT',
        data: { hidden },
      }),
      invalidatesTags: ['Modifiers'],
    }),
    setModifierHidden: builder.mutation<
      ModifierHideResult,
      { orgId: string; modifierId: string; hidden: boolean }
    >({
      query: ({ orgId, modifierId, hidden }) => ({
        url: mouseUrls.modifierHidden(orgId, modifierId),
        method: 'PUT',
        data: { hidden },
      }),
      invalidatesTags: ['Modifiers'],
    }),
    deleteModifier: builder.mutation<
      unknown,
      { orgId: string; modifierId: string }
    >({
      query: ({ orgId, modifierId }) => ({
        url: mouseUrls.modifier(orgId, modifierId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Modifiers', 'Products', 'Product'],
    }),
    /**
     * Pull the merchant's live Square catalogue into this catalogue. Starts a job
     * (202) that the dialog polls with `getCatalogueSyncJob`; a large merchant
     * takes longer than a request should. Invalidation happens when the job is
     * seen to finish, not here.
     */
    startCatalogueSync: builder.mutation<
      SyncJob,
      { orgId: string; catalogueId: string }
    >({
      query: ({ orgId, catalogueId }) => ({
        url: mouseUrls.catalogueSync(orgId, catalogueId),
        method: 'POST',
      }),
    }),
    getCatalogueSyncJob: builder.query<
      SyncJob,
      { orgId: string; catalogueId: string; jobId: string }
    >({
      query: ({ orgId, catalogueId, jobId }) => ({
        url: mouseUrls.catalogueSyncJob(orgId, catalogueId, jobId),
        method: 'GET',
      }),
    }),
    getCatalogueProducts: builder.query<
      CursorPage<Product>,
      { orgId: string } & ProductsQuery
    >({
      query: ({
        orgId,
        catalogueId,
        venue,
        search,
        cursor,
        limit,
        soldHere,
        squareRef,
      }) => ({
        url: mouseUrls.catalogueProducts(orgId, catalogueId, {
          venue,
          search,
          cursor,
          limit,
          ...(squareRef ? { square_ref: squareRef } : {}),
          // Only sent when true: the flag means nothing without a venue.
          ...(soldHere && venue ? { sold_here: 'true' as const } : {}),
        }),
        method: 'GET',
      }),
      transformResponse: (
        raw: Product[] | { data: Product[]; next_cursor?: string | null }
      ) => toCursorPage(raw),
      providesTags: ['Products'],
    }),
    getProduct: builder.query<
      ProductDetail,
      { orgId: string; productId: string; venue?: string }
    >({
      query: ({ orgId, productId, venue }) => ({
        url: mouseUrls.product(orgId, productId, venue),
        method: 'GET',
      }),
      providesTags: (_r, _e, { productId }) => [
        { type: 'Product', id: productId },
      ],
    }),
    patchProduct: builder.mutation<
      Product,
      { orgId: string; productId: string; body: PatchProductBody }
    >({
      query: ({ orgId, productId, body }) => ({
        url: mouseUrls.product(orgId, productId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    putProductTags: builder.mutation<
      { product_id: string; tag_ids: string[] },
      { orgId: string; productId: string; tagIds: string[] }
    >({
      query: ({ orgId, productId, tagIds }) => ({
        url: mouseUrls.productTags(orgId, productId),
        method: 'PUT',
        data: { tag_ids: tagIds },
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    /** Mouse takes the media list as the body itself, not wrapped in a key. */
    putProductMedia: builder.mutation<
      { product_id: string; media: Media[] },
      { orgId: string; productId: string; media: Media[] }
    >({
      query: ({ orgId, productId, media }) => ({
        url: mouseUrls.productMedia(orgId, productId),
        method: 'PUT',
        data: media,
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    putProductOverride: builder.mutation<
      Product,
      {
        orgId: string
        productId: string
        serial: string
        body: ProductOverrideBody
      }
    >({
      query: ({ orgId, productId, serial, body }) => ({
        url: mouseUrls.productOverride(orgId, productId, serial),
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: (_r, _e, { productId }) => [
        'Products',
        { type: 'Product', id: productId },
      ],
    }),
    getCatalogueModifierGroups: builder.query<
      ModifierGroup[],
      { orgId: string; catalogueId: string }
    >({
      query: ({ orgId, catalogueId }) => ({
        url: mouseUrls.catalogueModifierGroups(orgId, catalogueId),
        method: 'GET',
      }),
      transformResponse: (raw: ModifierGroup[] | { data: ModifierGroup[] }) =>
        toList(raw),
      providesTags: ['Modifiers'],
    }),
    patchModifierGroup: builder.mutation<
      ModifierGroup,
      { orgId: string; groupId: string; body: PatchModifierGroupBody }
    >({
      query: ({ orgId, groupId, body }) => ({
        url: mouseUrls.modifierGroup(orgId, groupId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: ['Modifiers'],
    }),
    patchModifier: builder.mutation<
      Modifier,
      { orgId: string; modifierId: string; body: PatchModifierBody }
    >({
      query: ({ orgId, modifierId, body }) => ({
        url: mouseUrls.modifier(orgId, modifierId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: ['Modifiers'],
    }),
    putModifierTags: builder.mutation<
      Modifier,
      { orgId: string; modifierId: string; tagIds: string[] }
    >({
      query: ({ orgId, modifierId, tagIds }) => ({
        url: mouseUrls.modifierTags(orgId, modifierId),
        method: 'PUT',
        data: { tag_ids: tagIds },
      }),
      invalidatesTags: ['Modifiers'],
    }),
    getTags: builder.query<Tag[], { orgId: string; kind?: string }>({
      query: ({ orgId, kind }) => ({
        url: mouseUrls.tags(orgId, kind),
        method: 'GET',
      }),
      transformResponse: (raw: Tag[] | { data: Tag[] }) => toList(raw),
      providesTags: ['Catalogue'],
    }),
    createTag: builder.mutation<Tag, { orgId: string; body: CreateTagBody }>({
      query: ({ orgId, body }) => ({
        url: mouseUrls.tags(orgId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Catalogue'],
    }),

    // ── Ordering points ─────────────────────────────────────────────────────
    getPoints: builder.query<
      OrderingPoint[],
      { orgId: string; venue?: string }
    >({
      query: ({ orgId, venue }) => ({
        url: mouseUrls.points(orgId, venue),
        method: 'GET',
      }),
      transformResponse: (raw: OrderingPoint[] | { data: OrderingPoint[] }) =>
        toList(raw),
      providesTags: ['Points'],
    }),
    /**
     * How a venue serves an order, and its own wording above the table
     * picker. A venue that has overridden nothing answers the empty shape, so
     * there is no 404 to handle.
     */
    getVenueSettings: builder.query<
      OrderingVenueSettings,
      { orgId: string; serial: string }
    >({
      query: ({ orgId, serial }) => ({
        url: mouseUrls.venueSettings(orgId, serial),
        method: 'GET',
      }),
      providesTags: ['VenueSettings'],
    }),
    patchVenueSettings: builder.mutation<
      OrderingVenueSettings,
      {
        orgId: string
        serial: string
        body: Partial<
          Pick<
            OrderingVenueSettings,
            | 'fulfilment_mode'
            | 'picker_note'
            | 'notify_ready'
            | 'prep_seconds_default'
            | 'capacity'
          >
        >
      }
    >({
      query: ({ orgId, serial, body }) => ({
        url: mouseUrls.venueSettings(orgId, serial),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: ['VenueSettings'],
    }),
    /**
     * The zone names in use per venue. Tagged 'Points' because Mouse derives
     * zones from the points, so renaming a table's zone refreshes this too.
     */
    getZones: builder.query<VenueZones[], { orgId: string; venue?: string }>({
      query: ({ orgId, venue }) => ({
        url: mouseUrls.zones(orgId, venue),
        method: 'GET',
      }),
      transformResponse: (raw: VenueZones[] | { data: VenueZones[] }) =>
        toList(raw),
      providesTags: ['Points'],
    }),
    createPoint: builder.mutation<
      OrderingPoint,
      { orgId: string; body: CreatePointBody }
    >({
      query: ({ orgId, body }) => ({
        url: mouseUrls.points(orgId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Points'],
    }),
    patchPoint: builder.mutation<
      OrderingPoint,
      { orgId: string; pointId: string; body: PatchPointBody }
    >({
      query: ({ orgId, pointId, body }) => ({
        url: mouseUrls.point(orgId, pointId),
        method: 'PATCH',
        data: body,
      }),
      invalidatesTags: ['Points'],
    }),
    deletePoint: builder.mutation<unknown, { orgId: string; pointId: string }>({
      query: ({ orgId, pointId }) => ({
        url: mouseUrls.point(orgId, pointId),
        method: 'DELETE',
      }),
      invalidatesTags: ['Points'],
    }),
    rotatePointToken: builder.mutation<
      OrderingPoint,
      { orgId: string; pointId: string }
    >({
      query: ({ orgId, pointId }) => ({
        url: mouseUrls.pointRotate(orgId, pointId),
        method: 'POST',
      }),
      invalidatesTags: ['Points'],
    }),
    bulkCreatePoints: builder.mutation<
      BulkPointsResult,
      { orgId: string; body: BulkPointsBody }
    >({
      query: ({ orgId, body }) => ({
        url: mouseUrls.pointsBulk(orgId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Points'],
    }),
    getPointQr: builder.query<PointQr, { orgId: string; pointId: string }>({
      query: ({ orgId, pointId }) => ({
        url: mouseUrls.pointQr(orgId, pointId),
        method: 'GET',
      }),
      // A rotated token changes the URL, so the QR follows the points list.
      providesTags: ['Points'],
    }),

    // ── Orders ──────────────────────────────────────────────────────────────
    getOrders: builder.query<
      CursorPage<OrderSummary>,
      { orgId: string } & OrdersQuery
    >({
      query: ({
        orgId,
        venue,
        status,
        payment_status,
        channel,
        context_type,
        date_field,
        from,
        to,
        q,
        cursor,
        limit,
      }) => ({
        url: mouseUrls.orders(orgId, {
          venue,
          status,
          payment_status,
          channel,
          context_type,
          date_field,
          from,
          to,
          q,
          cursor,
          limit,
        }),
        method: 'GET',
      }),
      transformResponse: (
        raw:
          | OrderSummary[]
          | { data: OrderSummary[]; next_cursor?: string | null }
      ) => toCursorPage(raw),
      providesTags: ['Orders'],
    }),

    /**
     * THE SAME ORDERS, FOLDED.
     *
     * Takes the orders query verbatim plus `group_by`, so a folded view and
     * the list above it can never describe different orders. The folding is
     * Mouse's: the page holds fifty rows and the question has thousands, so
     * grouping here would answer about a page and look like an answer about
     * the table.
     */
    getOrdersSummary: builder.query<
      OrdersSummary,
      { orgId: string } & OrdersSummaryQuery
    >({
      query: ({
        orgId,
        venue,
        status,
        payment_status,
        channel,
        context_type,
        date_field,
        from,
        to,
        group_by,
      }) => ({
        url: mouseUrls.ordersSummary(orgId, {
          venue,
          status,
          payment_status,
          channel,
          context_type,
          date_field,
          from,
          to,
          group_by,
        }),
        method: 'GET',
      }),
      providesTags: ['Orders'],
    }),
    getOrder: builder.query<Order, { orgId: string; orderId: string }>({
      query: ({ orgId, orderId }) => ({
        url: mouseUrls.order(orgId, orderId),
        method: 'GET',
      }),
      transformResponse: (raw: Order | { order: Order }) =>
        'order' in raw && raw.order ? raw.order : (raw as Order),
      providesTags: (_r, _e, { orderId }) => [{ type: 'Order', id: orderId }],
    }),
    refundOrder: builder.mutation<
      RefundResult,
      { orgId: string; orderId: string; body: RefundBody }
    >({
      query: ({ orgId, orderId, body }) => ({
        url: mouseUrls.orderRefunds(orgId, orderId),
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_r, _e, { orderId }) => [
        'Orders',
        { type: 'Order', id: orderId },
      ],
    }),

    // ── Text messages ───────────────────────────────────────────────────────
    getOrderingMessages: builder.query<SmsLog, { orgId: string } & SmsLogQuery>(
      {
        query: ({ orgId, from, to, serial, outcome, cursor, limit }) => ({
          url: mouseUrls.messages(orgId, {
            from,
            to,
            serial,
            outcome,
            cursor,
            limit,
          }),
          method: 'GET',
        }),
        providesTags: ['Messages'],
      }
    ),

    // ── App feedback (Stampede only) ────────────────────────────────────────
    getAppFeedback: builder.query<
      AppFeedback,
      { orgId: string } & AppFeedbackQuery
    >({
      query: ({
        orgId,
        from,
        to,
        serial,
        stars,
        with_comment,
        cursor,
        limit,
      }) => ({
        url: mouseUrls.appFeedback(orgId, {
          from,
          to,
          serial,
          stars,
          with_comment,
          cursor,
          limit,
        }),
        method: 'GET',
      }),
      providesTags: ['AppFeedback'],
    }),
  }),
})

export default mouseApi
